import { overlayRegistry } from '@/app/components/overlays/core/overlayRegistry';
import type { OverlayRegistrationProps } from '@/app/components/overlays/core/types';
import { getOverlayRegistryKey } from '@/app/components/overlays/utils/getOverlayRegistryKey';
import { safeCall } from '@/app/utils/safeCall';

const mountTriggers = new Map<string, () => void>();
const pendingFrameMap = new Map<string, number>();

/**
 * Registers a mount trigger if not already registered.
 * Used to mount overlays before opening or toggling.
 */
export const setOverlayMountTrigger = (
  props: OverlayRegistrationProps,
  mountCallback: () => void
): void => {
  const key = getOverlayRegistryKey(props);
  if (!mountTriggers.has(key)) {
    mountTriggers.set(key, mountCallback);
  }
};

/**
 * Unregisters a mount trigger to prevent memory leaks on dynamic overlays.
 */
export const unregisterOverlayMountTrigger = (
  props: OverlayRegistrationProps
): void => {
  const key = getOverlayRegistryKey(props);
  mountTriggers.delete(key);
};

/**
 * Ensures the overlay is mounted by calling its trigger if it exists.
 */
const runMountTrigger = (props: OverlayRegistrationProps): void => {
  const key = getOverlayRegistryKey(props);
  safeCall(mountTriggers.get(key));
};

/**
 * Ensures one frame-pending action per overlay key.
 */
const withPendingFrame = (key: string, action: () => void): void => {
  if (pendingFrameMap.has(key)) return;

  const frameId = requestAnimationFrame(() => {
    action();
    pendingFrameMap.delete(key);
  });

  pendingFrameMap.set(key, frameId);
};

/**
 * Opens the overlay. Mounts first if needed.
 */
export const openOverlay = (props: OverlayRegistrationProps): void => {
  const key = getOverlayRegistryKey(props);

  withPendingFrame(key, () => {
    const entry = overlayRegistry.map.get(key);
    if (!entry) {
      runMountTrigger(props);
      overlayRegistry.openQueue.add(key);
    } else if (!entry.isOpen()) {
      entry.setOpen(true);
    }
  });
};

/**
 * Toggles the overlay open state. Mounts first if needed.
 */
export const toggleOverlay = (props: OverlayRegistrationProps): void => {
  const key = getOverlayRegistryKey(props);

  withPendingFrame(key, () => {
    const entry = overlayRegistry.map.get(key);
    if (!entry) {
      runMountTrigger(props);
      overlayRegistry.toggleQueue.add(key);
    } else {
      entry.setOpen(!entry.isOpen());
    }
  });
};

/**
 * Closes the overlay if currently open.
 */
export const closeOverlay = (props: OverlayRegistrationProps): void => {
  const key = getOverlayRegistryKey(props);

  withPendingFrame(key, () => {
    const entry = overlayRegistry.map.get(key);
    if (entry?.isOpen()) {
      entry.setOpen(false);
    }
  });
};

/**
 * Closes a list of overlays.
 */
export const closeManyOverlays = (
  propsList: OverlayRegistrationProps[]
): void => {
  propsList.forEach(closeOverlay);
};

/**
 * Closes all registered overlays.
 */
export const closeAllOverlays = (): void => {
  overlayRegistry.map.forEach(({ setOpen }) => setOpen(false));
};
